import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
  Collapse,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { Config } from 'src/config';
import HttpServices from 'src/services/httpService';
import { integrateModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import ThreeDotLoader from 'src/utils/Loader/threeDotLoader.compontent';
import { useToaster } from 'src/utils/toaster/toasterContext';
import GBIcon from '../../../assets/google-my-business-logo.svg';
import CustomDomainRedirectIframe from '../customDomainIframe';
import GoogleMyBusinessAuthorizationModal from './google-my-business-authorization-modal';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useResellerLimitDialog } from 'src/components/reseller-limit-context/resellerLimitContext';

const GoogleMyBusinessIntegration = (props) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const { showResellerLimitDialog } = useResellerLimitDialog();
  const [isAuthorizationModalOpen, setAuthorizationModal] = useState(true);
  const [isGoogleMyBusinessAccountModalOpen, setGoogleMyBusinessAccountModal] = useState(false);
  const [isNoAccountForIntegrate, setNoAccountIntegrated] = useState(false);
  const [loaderReplies, setLoaderReplies] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [selectedPages, setSelectedPages] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false); // New state for submit button loading
  const { showToast } = useToaster();

  let isProcessing = false;
  const isCustomDomain = CommonHelper.CustomDomain();
  let intervalId = null;
  const messageListenerRef = useRef(null);

  useEffect(() => {
    intervalId = setInterval(() => {
      checkAccessTokenReceivedFromGoogleMyBusiness();
    }, Config.Common.socialAccountsIntegrationInterval);
    return () => {
      clearInterval(intervalId);
      cleanupListener();
    };
  }, []);

  const getGoogleMyBusinessOauthToken = () => {
    window.open(
      `${Config.GoogleMyBusiness.authorizeUrl}?client_id=${Config.GoogleMyBusiness.clientId}&scope=${Config.GoogleMyBusiness.scope}&prompt=consent&access_type=offline&include_granted_scopes=true&state=state_parameter_passthrough_value&redirect_uri=${Config.GoogleMyBusiness.redirectUri}&response_type=code`,
      '_blank'
    );
  };

  const cleanupListener = () => {
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current, false);
      messageListenerRef.current = null;
    }
  };

  const checkAccessTokenReceivedFromGoogleMyBusiness = () => {
    if (isCustomDomain) {
      const handleMessage = (event) => {
        const { type, code } = event.data;
        if (type === 'googleMyBusiness_token' && code && !isProcessing) {
          isProcessing = true;
          cleanupListener();
          clearInterval(intervalId);
          sendAccessTokenToServer(code);
        }
      };

      if (!messageListenerRef.current) {
        window.addEventListener('message', handleMessage, false);
        messageListenerRef.current = handleMessage;
      }
    } else {
      var token = localStorage.getItem('googleMyBusiness_token');
      var error = localStorage.getItem('googleMyBusiness_error');
      if (token) {
        localStorage.removeItem('googleMyBusiness_token');
        clearInterval(intervalId);
        sendAccessTokenToServer(token);
      } else if (error) {
        localStorage.removeItem('googleMyBusiness_error');
        clearInterval(intervalId);
        showToast(error, 'e');
      }
    }
  };

  const checkAccountIsIntegrated = (accountId, page) => {
    if (page.is_integrated) return true;
    const selectedAccountPages = selectedPages[accountId] || [];
    return selectedAccountPages.some((p) => p.id === page.id);
  };

  const handlePages = (accountId, pageId) => {
    setSelectedPages((prev) => {
      const selectedAccountPages = [...(prev[accountId] || [])];
      const index = selectedAccountPages.findIndex((p) => p.id === pageId);
      if (index >= 0) {
        selectedAccountPages.splice(index, 1);
      } else {
        const selectedPage = accounts.find((a) => a.id === accountId).pages.find((p) => p.id === pageId);
        selectedAccountPages.push(selectedPage);
      }
      return { ...prev, [accountId]: selectedAccountPages };
    });
  };

  const SmallAvatar = styled(Avatar)(({ theme }) => ({
    width: 22,
    height: 22,
    overflow: 'hidden',
  }));

  const myBusinessSuccessCallback = (data, message) => {
    CommonHelper.EnsureConversionFactorFlag(CommonHelper.ConversionFlagList.FIRST_SOCIAL_ACCOUNT_CONNECTED,"GOOGLEMYBUSINESS")
    setIsSubmitting(false); // Reset loading state
    showToast(message, 's');
    handleClose();
  };

  const googleMyBusinessFailureCallback = (message) => {
    setIsSubmitting(false); // Reset loading state
    if (!showResellerLimitDialog(message)) {
      message && showToast(message, 'e');
    }
    handleClose();
  };

  const handleSubmitPages = () => {
    setIsSubmitting(true); // Set loading state
    const integrationData = Object.keys(selectedPages).map((accountId) => ({
      id: accountId,
      pages: selectedPages[accountId],
    }));
    let postProps = {
      url: integrateModuleURL.integrateGoogleMyBusinessAccessToken,
      body: { all_pages: integrationData },
      successCallback: myBusinessSuccessCallback,
      failureCallback: googleMyBusinessFailureCallback,
    };
    HttpServices.Post(postProps);
  };

  const googleMyBusinessSuccessCallback = (data, message) => {
    if (data.all_pages && data.all_pages.length > 0) {
      setAccounts(data.all_pages);
      const isNoAccountForIntegrate = !data.all_pages.some((account) => account.pages.some((p) => !p.is_integrated));
      setNoAccountIntegrated(isNoAccountForIntegrate);
      setGoogleMyBusinessAccountModal(true);
      setAuthorizationModal(false);
    } else {
      showToast(translate('integration.google_my_business.no_accounts_found'), 'e');
      handleClose();
    }
  };

  const googleMyBusinessMoreSuccessCallback = (accountId) => (data, message) => {
    setLoaderReplies((prev) => ({ ...prev, [accountId]: false }));
    setAccounts((prev) =>
      prev.map((account) =>
        account.id === accountId
          ? { ...account, pages: [...account.pages, ...data.pages], pagination: data.pagination }
          : account
      )
    );
    const updatedAccounts = accounts.map((account) =>
      account.id === accountId
        ? { ...account, pages: [...account.pages, ...data.pages], pagination: data.pagination }
        : account
    );
    const isNoAccountForIntegrate = !updatedAccounts.some((account) => account.pages.some((p) => !p.is_integrated));
    setNoAccountIntegrated(isNoAccountForIntegrate);
  };

  const googleMyBusinessMoreFailureCallback = (accountId) => (message) => {
    showToast(message, 'e');
    setLoaderReplies((prev) => ({ ...prev, [accountId]: false }));
  };

  const sendAccessTokenToServer = (code) => {
    let googleMyBusinessForm = { code };
    let postProps = {
      url: integrateModuleURL.sendGoogleMyBusinessAccessToken,
      body: googleMyBusinessForm,
      successCallback: googleMyBusinessSuccessCallback,
      failureCallback: googleMyBusinessFailureCallback,
    };
    HttpServices.Post(postProps);
  };

  const fetchNextPagesFromServer = (accountId, pagination) => {
    setLoaderReplies((prev) => ({ ...prev, [accountId]: true }));
    let googleMyBusinessForm = {
      temp_connected_profile_id: accountId,
      pagination,
    };
    let postProps = {
      url: integrateModuleURL.fetchGoogleMyBusinessNextPages,
      body: googleMyBusinessForm,
      successCallback: googleMyBusinessMoreSuccessCallback(accountId),
      failureCallback: googleMyBusinessMoreFailureCallback(accountId),
    };
    HttpServices.Post(postProps);
  };

  const handleClose = () => {
    cleanupListener();
    props.handleClose();
  };

  const [expandedAccounts, setExpandedAccounts] = useState({});

  const handleToggleAccount = (accountId) => {
    setExpandedAccounts((prev) => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  return (
    <>
      {isAuthorizationModalOpen && (
        <GoogleMyBusinessAuthorizationModal
          handleConnectGoogleMyBusiness={getGoogleMyBusinessOauthToken}
          handleClose={handleClose}
        />
      )}
      {isGoogleMyBusinessAccountModalOpen && (
        <Dialog onClose={handleClose} open={isGoogleMyBusinessAccountModalOpen} fullWidth maxWidth="md">
          <DialogTitle>{translate('integration.google_my_business.google_my_business_pages')}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ minHeight: '220px' }}>
              {accounts && accounts.length > 0 ? (
                <List>
                  {accounts.map((account) => (
                    <Box key={account.id} sx={{ mb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <ListItem
                        secondaryAction={
                          <IconButton onClick={() => handleToggleAccount(account.id)}>
                            {expandedAccounts[account.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={account.name}
                          secondary={`Type: ${account.type}`}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                      </ListItem>
                      <Collapse in={expandedAccounts[account.id]} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 4 }}>
                          {account.pages.map((page) => (
                            <ListItem
                              key={page.id}
                              secondaryAction={
                                <Checkbox
                                  edge="end"
                                  value={page.id}
                                  checked={checkAccountIsIntegrated(account.id, page)}
                                  onClick={() => handlePages(account.id, page.id)}
                                  disabled={page.is_integrated || page.verified === 'FAILED'}
                                />
                              }
                              disablePadding
                            >
                              <ListItemButton
                                onClick={() => handlePages(account.id, page.id)}
                                disabled={page.is_integrated || page.verified === 'FAILED'}
                              >
                                <ListItemAvatar>
                                  <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    badgeContent={<SmallAvatar alt="gbusiness" src={GBIcon} variant="rounded" />}
                                  >
                                    <Avatar
                                      src={page.profile_image ? page.profile_image : page.name || page.id}
                                      alt={page.name ? page.name : page.id}
                                      style={{ color: '#ffffff' }}
                                    />
                                  </Badge>
                                </ListItemAvatar>
                                <ListItemText primary={page.name} secondary={page.description} />
                                {page.verified === 'COMPLETED' ? (
                                  <Chip label={translate('common.verified')} color="primary" />
                                ) : (
                                  <Chip label={translate('common.UnVerified')} color="error" />
                                )}
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                        {account.pagination && (
                          <Box sx={{ textAlign: 'center', mt: 2 }}>
                            {loaderReplies[account.id] ? (
                              <ThreeDotLoader />
                            ) : (
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 700,
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  color: theme.palette.grey[500],
                                }}
                                onClick={() => fetchNextPagesFromServer(account.id, account.pagination)}
                              >
                                {translate('integration.next_pages')}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Collapse>
                    </Box>
                  ))}
                  {isNoAccountForIntegrate && (
                    <Typography sx={{ color: 'red', fontSize: '12px', mt: 2 }}>
                      {translate('common.accounts_integrated')}
                    </Typography>
                  )}
                </List>
              ) : (
                <Typography sx={{ color: 'red', margin: '10px' }}>
                  {translate('integration.google_my_business.no_accounts_found')}
                </Typography>
              )}
              {isCustomDomain && <CustomDomainRedirectIframe serviceName="gbusiness" />}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button color="error" variant="outlined" onClick={handleClose}>
              {translate('common.cancel')}
            </Button>
            {accounts.some((account) => account.pages.length > 0) && !isNoAccountForIntegrate && (
              <LoadingButton
                variant="contained"
                color="primary"
                disabled={Object.values(selectedPages).every((pages) => pages.length === 0)}
                autoFocus
                onClick={handleSubmitPages}
                loading={isSubmitting} // Bind loading state
              >
                {translate('common.submit')}
              </LoadingButton>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default GoogleMyBusinessIntegration;
